import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';

import { cn } from '@/lib/utils';

/**
 * Every call to action on the page.
 *
 * Deliberately the only landing file that routes, so "where does the primary
 * CTA point?" has exactly one answer to audit. Everything lands on `/login`:
 * this app is invitation-only, so there is no `/register` to send anyone to.
 * The copy and the destination therefore do not agree — "Book a working
 * session" opens a sign-in page — which is the known cost of shipping the
 * design before a signup or booking flow exists.
 *
 * `mustard` is the brand board's single reserved colour — "the one action we
 * want taken" — so a screen should carry at most one per view. Inside an
 * oxblood or ink band `--primary` is already mustard, which is why `mustard`
 * and `primary` resolve to the same thing there and the variant still reads
 * correctly.
 */
const variantClass = {
    quiet: 'text-muted-foreground hover:text-foreground',
    mustard: 'bg-brand-mustard text-brand-ink hover:bg-brand-mustard/85',
    outline:
        'border-[1.5px] border-foreground bg-transparent text-foreground hover:bg-foreground hover:text-background',
} as const;

type Props = {
    href: string;
    variant?: keyof typeof variantClass;
    className?: string;
    children: ReactNode;
};

export function CtaPill({
    href,
    children,
    className,
    variant = 'mustard',
}: Props) {
    const classes = cn(
        'inline-flex items-center justify-center rounded-full px-7 py-4 font-mono text-[12px] tracking-[0.08em] uppercase transition-colors duration-200',
        variantClass[variant],
        className,
    );

    // An in-page anchor is not a route. Handing "#security" to the router
    // resolves it against the route tree and navigates away from the page
    // instead of scrolling down it.
    if (href.startsWith('#')) {
        return (
            <a href={href} className={classes}>
                {children}
            </a>
        );
    }

    return (
        <Link to={href} className={classes}>
            {children}
        </Link>
    );
}
